import React, { useRef, useState, useEffect, useMemo } from "react";
import { fleetrifyIcons } from "../../../assets/icons/icons";
import svg from "../../../assets/svg";
import Skeleton from "react-loading-skeleton";
import {
  useGetSingleUserConversationQuery,
  useSendMessageMutation,
} from "../../../redux/services/messageApi";
import { getAvatarColor, getInitials } from "../../../utils/avatar";
import { createEcho } from "../../../utils/echo";
import { TypingIndicator } from "./TypingIndicator";
import { useSingleUserController } from "../../../controllers/useSingleUserController";
import { useGetRolePermissionsQuery } from "../../../redux/services/staffApi";
import { toast } from "sonner";
import { usePermission } from "../../../hooks/usePermission";

export const ChatWindow = ({
  selectedDetails,
  onBack,
}: {
  selectedDetails: any;
  onBack?: () => void;
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const [message, setMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [tempMessages, setTempMessages] = useState<any[]>([]);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [typingName, setTypingName] = useState<string>("");
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const whisperThrottleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const partnerChannelRef = useRef<any>(null);
  const partnerSubscribedRef = useRef(false);

  const sender_id = localStorage.getItem("userId") || "";
  const receiver_id = selectedDetails?.id;
  const senderName = (() => {
    try {
      return JSON.parse(localStorage.getItem("userDetails") || "{}").name || "";
    } catch {
      return "";
    }
  })();

  const { data: conversation, isLoading } = useGetSingleUserConversationQuery(
    {
      sender_id,
      receiver_id,
      sender_type: "client",
      receiver_type: "client",
    },
    {
      skip: !selectedDetails,
    }
  );

  const { hasPermission } = usePermission();
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // prevent new line
      handleSend();
    }
  };


  const messagesData = useMemo(
    () => conversation?.data || [],
    [conversation?.data]
  );

  const [sendMessage] = useSendMessageMutation();

  /* 🧹 Remove optimistic duplicates */
  useEffect(() => {
    if (!messagesData.length) return;

    setTempMessages((prev) =>
      prev.filter(
        (temp) =>
          !messagesData.some(
            (real: any) =>
              real.message === temp.message && real.sender_id === temp.sender_id
          )
      )
    );
  }, [messagesData]);

  const finalMessages = [...messagesData, ...tempMessages];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
  }, [finalMessages.length]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !sender_id || !receiver_id) return;

    const echo = createEcho(token);
    const channelName = `chat.client.${sender_id}`;

    const ownChannel = echo.private(channelName);

    // Subscribe to partner's channel so we can whisper on it.
    // Track subscription_succeeded so we never whisper before auth completes.
    const partnerChannelName = `chat.client.${receiver_id}`;
    const partnerCh = echo.private(partnerChannelName);
    partnerChannelRef.current = partnerCh;
    partnerSubscribedRef.current = false;

    // partnerCh.subscription is the raw Pusher channel object
    if (partnerCh.subscription?.subscribed) {
      // Already subscribed from a previous mount (channel reuse)
      partnerSubscribedRef.current = true;
    } else {
      partnerCh.subscription?.bind("pusher:subscription_succeeded", () => {
        partnerSubscribedRef.current = true;
      });
    }

    const messageListener = (event: any) => {
      if (event.sender_id === sender_id) return;
      if (event.sender_id !== receiver_id) return;
      setTempMessages((prev) => [...prev, event]);
    };

    ownChannel.listen(".message.sent", messageListener);

    // Receive typing whispers from partner on OWN channel
    ownChannel.listenForWhisper("typing", (event: any) => {
      if (event.sender_id !== receiver_id) return;
      setTypingName(event.sender_name || "");
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => setTypingName(""), 3000);
    });

    return () => {
      ownChannel.stopListening(".message.sent");
      ownChannel.stopListening("whisper:typing" as any);
      echo.leave(partnerChannelName);
      partnerChannelRef.current = null;
      partnerSubscribedRef.current = false;
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (whisperThrottleRef.current) clearTimeout(whisperThrottleRef.current);
      setTypingName("");
    };
  }, [sender_id, receiver_id]);

  if (!selectedDetails) {
    return (
      <div className="flex flex-col justify-center items-center w-full hidden lg:flex">
        <img src="/images/no-conversation.svg" alt="No conversation" className="mb-4" />
        {/* <p className="text-gray-500">Select a conversation to start chatting!</p> */}
        <p className="text-gray-500">No messages yet. Type a message below to begin your chat.</p>
      </div>
    );
  }

  const handleInput = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  };

  const handleTyping = () => {
    // Only whisper after channel auth is confirmed — avoids "not subscribed" error
    if (whisperThrottleRef.current || !partnerChannelRef.current || !partnerSubscribedRef.current) return;

    partnerChannelRef.current.whisper("typing", {
      sender_id,
      sender_name: senderName,
    });

    // Throttle to once every 2 seconds so we don't spam Pusher
    whisperThrottleRef.current = setTimeout(() => {
      whisperThrottleRef.current = null;
    }, 2000);
  };

  /* 📎 FILE SELECTION */
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setSelectedFiles((prev) => [...prev, ...files]);
    e.target.value = "";
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const sendSingleMessage = async ({
    text,
    file,
  }: {
    text?: string;
    file?: File;
  }) => {
    const tempId = "temp-" + Date.now() + Math.random();

    setTempMessages((prev) => [
      ...prev,
      {
        id: tempId,
        sender_id,
        receiver_id,
        message: text || "",
        created_at: new Date().toISOString(),
        status: "pending",
        files: file
          ? [
              {
                name: file.name,
                type: file.type,
                preview: file.type.startsWith("image/")
                  ? URL.createObjectURL(file)
                  : null,
                originalFile: file,
              },
            ]
          : [],
      },
    ]);

    const formData = new FormData();
    formData.append("sender_id", sender_id);
    formData.append("sender_type", "client");
    formData.append("receiver_id", receiver_id);
    formData.append("receiver_type", "client");
    if (text) formData.append("message", text);
    if (file) formData.append("file", file);

    try {
      await sendMessage(formData).unwrap();
    } catch {
      setTempMessages((prev) =>
        prev.map((m) => (m.id === tempId ? { ...m, status: "failed" } : m))
      );
    }
  };

  const handleSend = async () => {
    if (!hasPermission("send message to client")) {
                toast.error("You don't have permission to send message to client");
                return;
            }
    if (!message.trim() && selectedFiles.length === 0) return;

    if (selectedFiles.length > 0) {
      for (let i = 0; i < selectedFiles.length; i++) {
        await sendSingleMessage({
          text: i === 0 ? message : undefined,
          file: selectedFiles[i],
        });
      }
    } else {
      await sendSingleMessage({ text: message });
    }

    setMessage("");
    setSelectedFiles([]);
  };

  const retrySend = async (msg: any) => {
    const file = msg.files?.[0]?.originalFile;

    await sendSingleMessage({
      text: msg.message,
      file,
    });

    setTempMessages((prev) => prev.filter((m) => m.id !== msg.id));
  };

  const getMessageFiles = (msg: any) => {
    if (msg.files && msg.files.length > 0) return msg.files;

    if (msg.media && msg.media.length > 0) {
      return msg.media.map((m: any) => ({
        name: m.file_name || m.name,
        type: m.mime_type,
        preview: m.mime_type?.startsWith("image/") ? m.original_url : null,
        url: m.original_url,
      }));
    }

    if (msg.file_url) {
      return [
        {
          name: "Attachment",
          type: "",
          preview: msg.file_url,
          url: msg.file_url,
        },
      ];
    }

    return [];
  };

  return (
    <div
      className={`flex-1 flex flex-col m-4 bg-[#fff] ${
        selectedDetails ? "block" : "hidden lg:block"
      }`}
    >
      {/* HEADER */}
      <div className="flex items-center gap-3 p-4 border-b">
        <button onClick={onBack} className="lg:hidden text-[#143887]">
          ←
        </button>

        {selectedDetails?.profile_image ? (
          <img
            src={selectedDetails.profile_image}
            alt={selectedDetails?.name}
            className="h-[36px] w-[36px] rounded-full object-cover"
          />
        ) : (
          <div
            className="h-[36px] w-[36px] rounded-full flex items-center justify-center text-white text-sm font-semibold"
            style={{ backgroundColor: getAvatarColor(selectedDetails?.name) }}
          >
            {getInitials(selectedDetails?.name)}
          </div>
        )}

        <h2 className="text-sm font-semibold">{selectedDetails?.name}</h2>
      </div>

      {/* MESSAGES */}
      <div
        className="flex-1 overflow-y-auto p-5 space-y-4"
        style={{ backgroundImage: `url(${svg.ChatBg})` }}
      >
        {isLoading ? (
          <Skeleton height={50} count={4} />
        ) : finalMessages.length === 0 ? (
          <div className="text-center text-gray-500">
            No messages yet. Start the conversation!
          </div>
        ) : (
          finalMessages.map((msg: any) => {
            const isSender = msg.sender_id === sender_id;

            return (
              <div
                key={msg.id}
                className={`flex flex-col ${
                  isSender ? "items-end" : "items-start"
                }`}
              >
                <div className="text-xs text-gray-500 mb-1">
                  {isSender ? "You" : msg.sender?.name}
                </div>

                <div
                  className={`relative max-w-[70%] px-4 py-3 pr-14 rounded-lg shadow text-sm ${
                    isSender ? "bg-[#F5F6FA]" : "bg-[#E7E9EE]"
                  }`}
                >
                  {getMessageFiles(msg).map((f: any, i: number) =>
                    f.preview ? (
                      <img
                        key={i}
                        src={f.preview}
                        className="max-w-[220px] rounded mb-2 cursor-pointer hover:opacity-90"
                        alt="attachment"
                        onClick={() => setPreviewImage(f.preview)}
                      />
                    ) : (
                      <a
                        key={i}
                        href={f.url}
                        target="_blank"
                        rel="noreferrer"
                        className="block text-xs mb-1 text-blue-600 underline"
                      >
                        📎 {f.name}
                      </a>
                    )
                  )}

                  {msg.message}

                  <span className="absolute -bottom-0.5 right-6 text-[10px]">
                    {new Date(msg.created_at).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>

                  {isSender && (
                    <span className="absolute -bottom-0.5 right-2 text-xs">
                      {msg.status === "pending" && "⏳"}
                      {msg.status === "sent" && "✓"}
                      {msg.status === "failed" && (
                        <button
                          onClick={() => retrySend(msg)}
                          className="text-red-600"
                        >
                          Resend ⚠
                        </button>
                      )}
                    </span>
                  )}
                </div>

                <div ref={messagesEndRef} />
              </div>
            );
          })
        )}

        {typingName && (
          <div className="flex flex-col items-start">
            <TypingIndicator name={typingName} />
          </div>
        )}
      </div>

      {/* INPUT */}
      <div className="p-4 border-t">
        {selectedFiles.length > 0 && (
          <div className="flex gap-2 flex-wrap mb-2">
            {selectedFiles.map((file, i) => (
              <div
                key={i}
                className="flex items-center gap-2 bg-gray-100 px-2 py-1 rounded text-xs"
              >
                {file.type.startsWith("image/") ? (
                  <img
                    src={URL.createObjectURL(file)}
                    className="h-20 w-20 object-cover rounded"
                  />
                ) : (
                  <span>📎 {file.name}</span>
                )}
                <button
                  onClick={() => removeSelectedFile(i)}
                  className="text-red-500"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex items-center gap-3">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => { setMessage(e.target.value); handleTyping(); }}
            onInput={handleInput}
            placeholder="Write a message..."
            onKeyDown={handleKeyDown}
            className="flex-1 resize-none text-sm outline-none"
            rows={1}
          />

            <button onClick={handleSend} disabled={(!message.trim() && selectedFiles.length === 0) || !hasPermission("send message to client")} title={!hasPermission("send message to client") ? "You don't have permission to send message to client" : undefined} className="text-[#143887] disabled:opacity-50 disabled:cursor-not-allowed">
            {fleetrifyIcons.sendIcon}
          </button>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={handleFileSelect}
          />

          <button
            onClick={() => fileInputRef.current?.click()}
            className="text-[#143887]"
          >
            {fleetrifyIcons.imageUploadIcon}
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="text-[#143887]"
          >
            {fleetrifyIcons.fileUploadIcon}
          </button>
        </div>
      </div>

      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-full rounded shadow-lg"
            />
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute -top-4 -right-4 bg-white text-black rounded-full w-8 h-8 flex items-center justify-center font-bold"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
